import React, { useEffect, useState } from "react";
import classes from "./recordAvatar.module.scss";

/** First letters of the first two words — shown when a record has no usable image. */
export const initialsOf = (name: string | null | undefined): string =>
    (name ?? "")
        .split(/\s+/)
        .filter(Boolean)
        .slice(0, 2)
        .map((word) => word[0])
        .join("")
        .toUpperCase() || "?";

export type RecordImageKind = "logo" | "portrait";

interface RecordAvatarProps {
    /** Record name — the initials come from it. */
    name: string;
    src?: string | null;
    /** `logo` is contained (never cropped) on a white tile; `portrait` covers the tile. */
    kind?: RecordImageKind;
    /** `large` = the card's rail tile, `small` = a table row's identity cell. */
    size?: "large" | "small";
    round?: boolean;
}

/**
 * The visual anchor of a record in lists: its photo or logo, or its initials.
 * Photo URLs are presigned and expire after an hour, so a list left open past
 * that would show broken images — a failed load falls back to the initials.
 */
const RecordAvatar: React.FC<RecordAvatarProps> = ({ name, src, kind = "logo", size = "large", round }) => {
    const [hasFailed, setHasFailed] = useState(false);
    // A refreshed search hands out a new presigned URL — give it a fresh chance.
    useEffect(() => setHasFailed(false), [src]);

    const shapeClasses = [
        classes.avatar,
        size === "small" ? classes.small : classes.large,
        // A round portrait stays square, or it would be an oval.
        size === "large" && kind === "portrait" && !round ? classes.largePortrait : "",
        round ? classes.round : "",
    ].join(" ");

    if (src && !hasFailed) {
        return (
            <div className={`${shapeClasses} ${kind === "portrait" ? classes.photo : classes.logo}`}>
                <img src={src} alt="" loading="lazy" onError={() => setHasFailed(true)} />
            </div>
        );
    }
    return (
        <div className={`${shapeClasses} ${classes.initials}`} aria-hidden="true">
            {initialsOf(name)}
        </div>
    );
};

export default RecordAvatar;
